if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        // Reinitialize parallax for new active image
        if (this.initActiveImageParallax) {
          setTimeout(() => {
            this.initActiveImageParallax();
          }, 100);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // Safari bug fixes:
        // 1. Force loading eager for lazy images that just became display block
        // 2. Bypass scroll-trigger bugs where IntersectionObserver fails to fire on display change
        if (activeMedia) {
          activeMedia.classList.add('scroll-trigger--cancel');
          activeMedia.classList.remove('scroll-trigger--offscreen');
          activeMedia.style.opacity = '1';

          const img = activeMedia.querySelector('img[loading="lazy"]');
          if (img) {
            img.setAttribute('loading', 'eager');
          }
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        // Reinitialize parallax for new active image
        if (this.initActiveImageParallax) {
          setTimeout(() => {
            this.initActiveImageParallax();
          }, 100);
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      connectedCallback() {
        // Initialize premium 3D tilt effects
        this.initPremiumImageEffects();

        // Initialize mobile-specific interactions
        this.initMobileInteractions();
      }

      initPremiumImageEffects() {
        // Only enable on desktop and if user hasn't reduced motion
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        if (!window.matchMedia('(min-width: 750px)').matches) return;

        // Initialize parallax effect for active image
        this.initActiveImageParallax();

        // Listen for active image changes
        this.elements.viewer.addEventListener('slideChanged', () => {
          this.initActiveImageParallax();
        });

        const imageWrappers = this.querySelectorAll('.premium-image-wrapper');
        const imageOpeners = this.querySelectorAll('.premium-image-opener');

        // Basic 3D Tilt Effect for Non-Active Images (subtle)
        imageOpeners.forEach((opener) => {
          const wrapper = opener.closest('.premium-image-wrapper');
          const mediaItem = wrapper?.closest('.product__media-item');
          if (!wrapper || !mediaItem) return;

          // Skip if this is the active image (it gets special parallax treatment)
          if (mediaItem.classList.contains('is-active')) return;

          opener.addEventListener('mousemove', (e) => {
            if (!window.matchMedia('(hover: hover)').matches) return;

            const rect = opener.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const centerX = rect.width / 2;
            const centerY = rect.height / 2;

            const rotateX = ((y - centerY) / centerY) * -5; // Max 5 degrees for non-active
            const rotateY = ((x - centerX) / centerX) * 5;

            wrapper.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-4px) scale(1.01)`;
          });

          opener.addEventListener('mouseleave', () => {
            wrapper.style.transform = '';
          });
        });

        // Intersection Observer for scroll-triggered animations
        if ('IntersectionObserver' in window) {
          const observerOptions = {
            threshold: 0.1,
            rootMargin: '50px'
          };

          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('animate-in');
                observer.unobserve(entry.target);
              }
            });
          }, observerOptions);

          imageWrappers.forEach((wrapper) => {
            observer.observe(wrapper);
          });
        }

        // Enhanced thumbnail hover effects
        const thumbnails = this.querySelectorAll('.premium-thumbnail');
        thumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener('mouseenter', () => {
            // Add subtle pulse animation
            thumbnail.style.animation = 'premiumThumbnailPulse 0.6s ease-out';
          });
        });
      }

      initActiveImageParallax() {
        // Remove existing parallax listeners
        if (this.activeImageParallaxHandler) {
          const activeOpener = this.querySelector('.product__media-item.is-active .premium-image-opener');
          if (activeOpener) {
            activeOpener.removeEventListener('mousemove', this.activeImageParallaxHandler);
            activeOpener.removeEventListener('mouseleave', this.activeImageLeaveHandler);
            activeOpener.removeEventListener('click', this.activeImageClickHandler, true); // Remove with capture phase
          }
        }

        // Hide cursor if it exists
        const existingCursor = document.getElementById('product-image-custom-cursor');
        if (existingCursor) {
          existingCursor.style.display = 'none';
        }

        // Find the currently active image
        const activeMediaItem = this.querySelector('.product__media-item.is-active');
        if (!activeMediaItem) return;

        const activeOpener = activeMediaItem.querySelector('.premium-image-opener');
        const activeWrapper = activeMediaItem.querySelector('.premium-image-wrapper');
        const activeImage = activeMediaItem.querySelector('.premium-image-inner img');

        if (!activeOpener || !activeWrapper || !activeImage) return;

        // Create custom cursor element
        let customCursor = document.getElementById('product-image-custom-cursor');
        if (!customCursor) {
          customCursor = document.createElement('div');
          customCursor.id = 'product-image-custom-cursor';
          customCursor.className = 'product-image-custom-cursor';
          document.body.appendChild(customCursor);
        }

        // Store reference for cleanup
        this.customCursor = customCursor;

        // Enhanced parallax effect for active image with custom cursor
        this.activeImageParallaxHandler = (e) => {
          if (!window.matchMedia('(hover: hover)').matches) return;

          if (this.parallaxRafId) {
            cancelAnimationFrame(this.parallaxRafId);
          }

          this.parallaxRafId = requestAnimationFrame(() => {
            // Don't show cursor on mobile
            const isMobile = window.matchMedia('(max-width: 749px)').matches;

            const rect = activeOpener.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const centerX = rect.width / 2;
            const centerY = rect.height / 2;

            // Calculate normalized position (-1 to 1)
            const normalizedX = (x - centerX) / centerX;
            const normalizedY = (y - centerY) / centerY;

            // 3D tilt effect (more pronounced for active image)
            const rotateX = normalizedY * -12; // Max 12 degrees
            const rotateY = normalizedX * 12;

            // Parallax movement for the image inside (moves opposite to cursor)
            const moveX = normalizedX * 20; // Max 20px movement
            const moveY = normalizedY * 20;

            // Fix Safari hardware acceleration bugs
            activeWrapper.style.transformStyle = 'preserve-3d';
            activeWrapper.style.WebkitBackfaceVisibility = 'hidden';
            activeImage.style.WebkitBackfaceVisibility = 'hidden';

            // Apply 3D transform to wrapper (reduced scale)
            activeWrapper.style.transform = `
              perspective(1200px) 
              rotateX(${rotateX}deg) 
              rotateY(${rotateY}deg) 
              translateZ(0)
              translateY(-8px) 
              scale(1.01)
            `;

            // Apply parallax movement to image (reduced scale)
            activeImage.style.transform = `
              translate3d(${moveX}px, ${moveY}px, 0)
              scale(1.05)
            `;

            // Update custom cursor based on position (only on desktop)
            if (!isMobile && customCursor) {
              const isLeftHalf = x < centerX;
              customCursor.className = `product-image-custom-cursor ${isLeftHalf ? 'cursor-left' : 'cursor-right'}`;
              customCursor.style.left = `${e.clientX}px`;
              customCursor.style.top = `${e.clientY}px`;
              customCursor.style.display = 'block';
            } else if (customCursor) {
              customCursor.style.display = 'none';
            }
          });
        };

        this.activeImageLeaveHandler = () => {
          if (this.parallaxRafId) cancelAnimationFrame(this.parallaxRafId);

          // Smooth reset animation
          activeWrapper.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';
          activeImage.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';

          activeWrapper.style.transform = '';
          activeImage.style.transform = '';

          // Hide custom cursor
          if (customCursor) {
            customCursor.style.display = 'none';
          }

          // Remove transition after animation completes
          setTimeout(() => {
            activeWrapper.style.transition = '';
            activeImage.style.transition = '';

            // Critical for Safari: Clean up 3D context to prevent display:none bugs
            activeWrapper.style.transformStyle = '';
            activeWrapper.style.WebkitBackfaceVisibility = '';
            activeImage.style.WebkitBackfaceVisibility = '';
          }, 600);
        };

        // Click handler for navigation
        this.activeImageClickHandler = (e) => {
          // Don't handle clicks on mobile (let default behavior work)
          const isMobile = window.matchMedia('(max-width: 749px)').matches;
          if (isMobile) return;

          // Prevent modal from opening and other default behaviors
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const rect = activeOpener.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const centerX = rect.width / 2;

          const isLeftHalf = x < centerX;

          // Get all media items from the viewer (ordered as they appear in DOM)
          const allMediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
          if (allMediaItems.length <= 1) return; // Can't navigate if only one image

          // Find current active media
          const currentActiveMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
          if (!currentActiveMedia) return;

          const currentMediaId = currentActiveMedia.dataset.mediaId;
          const currentIndex = allMediaItems.findIndex(item => item.dataset.mediaId === currentMediaId);

          if (currentIndex === -1) return;

          let targetMediaId;

          if (isLeftHalf) {
            // Navigate to previous image (wrap around to last if at first)
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : allMediaItems.length - 1;
            targetMediaId = allMediaItems[prevIndex].dataset.mediaId;
          } else {
            // Navigate to next image (wrap around to first if at last)
            const nextIndex = currentIndex < allMediaItems.length - 1 ? currentIndex + 1 : 0;
            targetMediaId = allMediaItems[nextIndex].dataset.mediaId;
          }

          // Use setActiveMedia to change the active image
          if (targetMediaId) {
            this.setActiveMedia(targetMediaId, false);
          }
        };

        // Add event listeners - use capture phase for click to intercept before modal opens
        activeOpener.addEventListener('mousemove', this.activeImageParallaxHandler);
        activeOpener.addEventListener('mouseleave', this.activeImageLeaveHandler);
        activeOpener.addEventListener('click', this.activeImageClickHandler, true); // Use capture phase
      }

      initMobileInteractions() {
        // Only initialize on mobile
        if (window.matchMedia('(min-width: 750px)').matches) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const mediaList = this.elements.viewer?.querySelector('.product__media-list');
        if (!mediaList) return;

        let touchStartX = 0;
        let touchStartY = 0;
        let touchEndX = 0;
        let touchEndY = 0;
        let isSwiping = false;

        // Touch start
        mediaList.addEventListener('touchstart', (e) => {
          touchStartX = e.changedTouches[0].screenX;
          touchStartY = e.changedTouches[0].screenY;
          isSwiping = false;
        }, { passive: true });

        // Touch move - add visual feedback
        mediaList.addEventListener('touchmove', (e) => {
          if (!isSwiping) {
            isSwiping = true;
            mediaList.classList.add('swiping');
          }
        }, { passive: true });

        // Touch end - handle swipe
        mediaList.addEventListener('touchend', (e) => {
          touchEndX = e.changedTouches[0].screenX;
          touchEndY = e.changedTouches[0].screenY;

          mediaList.classList.remove('swiping');

          const deltaX = touchEndX - touchStartX;
          const deltaY = touchEndY - touchStartY;

          // Only handle horizontal swipes (more horizontal than vertical)
          if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
            const allMediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
            if (allMediaItems.length <= 1) return;

            const currentActiveMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
            if (!currentActiveMedia) return;

            const currentMediaId = currentActiveMedia.dataset.mediaId;
            const currentIndex = allMediaItems.findIndex(item => item.dataset.mediaId === currentMediaId);

            if (currentIndex === -1) return;

            let targetMediaId;

            if (deltaX > 0) {
              // Swipe right - go to previous
              const prevIndex = currentIndex > 0 ? currentIndex - 1 : allMediaItems.length - 1;
              targetMediaId = allMediaItems[prevIndex].dataset.mediaId;
            } else {
              // Swipe left - go to next
              const nextIndex = currentIndex < allMediaItems.length - 1 ? currentIndex + 1 : 0;
              targetMediaId = allMediaItems[nextIndex].dataset.mediaId;
            }

            if (targetMediaId) {
              // Add smooth transition class
              allMediaItems.forEach(item => {
                item.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
              });

              this.setActiveMedia(targetMediaId, false);

              // Remove transition after animation
              setTimeout(() => {
                allMediaItems.forEach(item => {
                  item.style.transition = '';
                });
              }, 400);
            }
          }

          isSwiping = false;
        }, { passive: true });

        // Add momentum scrolling enhancement
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('mobile-visible');
              }
            });
          }, {
            threshold: 0.3,
            rootMargin: '20px'
          });

          const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
          mediaItems.forEach(item => observer.observe(item));
        }

        // Enhanced thumbnail touch feedback
        const thumbnails = this.querySelectorAll('.premium-thumbnail');
        thumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener('touchstart', () => {
            thumbnail.style.transition = 'transform 0.15s ease-out';
          }, { passive: true });
        });
      }
    }
  );
}

// Premium Thumbnail Pulse Animation
if (!document.getElementById('premium-animations-style')) {
  const style = document.createElement('style');
  style.id = 'premium-animations-style';
  style.textContent = `
    @keyframes premiumThumbnailPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
  `;
  document.head.appendChild(style);
}
